import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ProductCard from "@/components/base/ProductCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { products } from "@/mocks/products";

type Collection = {
  slug: string;
  name: string;
  tagline: string;
  intro: string;
  image: string;
  cover: string;
  productIds: string[];
};

const collections: Collection[] = [
  {
    slug: "form-01",
    name: "Form 01",
    tagline: "Sculptural essentials",
    intro:
      "Our founding study in weight, curve and quiet architecture. Each piece in Form 01 is cast from a single hand-carved wax and finished with a soft satin surface.",
    image:
      "https://readdy.ai/api/search-image?query=Wide%20editorial%20campaign%20photograph%20of%20sculptural%20contemporary%20fine%20jewelry%20arranged%20on%20a%20soft%20warm%20neutral%20stone%20surface%2C%20quiet%20luxury%20aesthetic%2C%20cinematic%20natural%20light%2C%20muted%20warm%20tones%2C%20minimal%20refined%20composition%2C%20premium%20jewelry%20brand%20photography&width=900&height=506&seq=aurelin-col-form01&orientation=landscape",
    cover: "https://readdy.ai/api/search-image?query=Editorial%20square%20campaign%20image%20of%20sculptural%20contemporary%20fine%20gold%20jewelry%20on%20a%20soft%20warm%20neutral%20stone%20surface%2C%20quiet%20luxury%20aesthetic%2C%20natural%20diffused%20light%2C%20muted%20warm%20tones%2C%20minimal%20refined%20composition%2C%20premium%20jewelry%20brand%20photography&width=420&height=420&seq=aurelin-col-cover-form01&orientation=squarish",
    productIds: ["p3", "p4", "p7", "p1"],
  },
  {
    slug: "everyday-metal",
    name: "Everyday Metal",
    tagline: "Pieces designed for daily wear",
    intro:
      "A quiet edit of weightless chains, cuffs and bands intended to be layered, worn and forgotten on the body. Every finish is engineered for a lifetime of use.",
    image:
      "https://readdy.ai/api/search-image?query=Wide%20editorial%20campaign%20photograph%20of%20minimal%20everyday%20fine%20gold%20and%20silver%20jewelry%20layered%20on%20a%20soft%20linen%20surface%20in%20warm%20natural%20daylight%2C%20quiet%20luxury%20aesthetic%2C%20muted%20warm%20neutral%20tones%2C%20refined%20contemporary%20jewelry%20campaign&width=900&height=506&seq=aurelin-col-everyday&orientation=landscape",
    cover: "https://readdy.ai/api/search-image?query=Editorial%20square%20campaign%20image%20of%20minimal%20everyday%20fine%20gold%20and%20silver%20chains%20on%20a%20soft%20warm%20linen%20surface%2C%20quiet%20luxury%20aesthetic%2C%20natural%20diffused%20daylight%2C%20muted%20warm%20neutral%20tones%2C%20refined%20contemporary%20jewelry%20campaign&width=420&height=420&seq=aurelin-col-cover-everyday&orientation=squarish",
    productIds: ["p2", "p6", "p8", "p5"],
  },
  {
    slug: "new-arrivals",
    name: "New Arrivals",
    tagline: "Latest objects from the studio",
    intro:
      "The most recent studies from the Antwerp atelier — small releases, made in limited runs and finished entirely by hand.",
    image:
      "https://readdy.ai/api/search-image?query=Wide%20editorial%20campaign%20photograph%20of%20new%20release%20contemporary%20fine%20jewelry%20on%20a%20softly%20lit%20warm%20beige%20studio%20surface%2C%20quiet%20luxury%20aesthetic%2C%20cinematic%20natural%20light%2C%20muted%20warm%20tones%2C%20minimal%20refined%20composition&width=900&height=506&seq=aurelin-col-new&orientation=landscape",
    cover: "https://readdy.ai/api/search-image?query=Editorial%20square%20campaign%20image%20of%20new%20release%20contemporary%20fine%20jewelry%20arranged%20on%20a%20softly%20lit%20warm%20beige%20studio%20surface%2C%20quiet%20luxury%20aesthetic%2C%20natural%20diffused%20light%2C%20muted%20warm%20tones%2C%20minimal%20refined%20composition&width=420&height=420&seq=aurelin-col-cover-new&orientation=squarish",
    productIds: ["p1", "p5", "p6", "p7"],
  },
];

export default function CollectionsIndex() {
  return (
    <Layout>
      <section>
        <div className="shell pt-4 md:pt-24 pb-6 md:pb-16">
          <Breadcrumb items={[{ label: "Collections" }]} />
          <h1 className="mt-4 md:mt-8 font-display text-4xl md:text-6xl text-black">The Aurelin Collections</h1>
          <p className="mt-4 md:mt-6 text-[14px] md:text-[15px] text-graphite max-w-xl leading-relaxed">
            Small, considered releases from our Antwerp atelier. Each collection is a self-contained study in material, form and wear.
          </p>
        </div>

        <div className="shell pb-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12">
            {collections.map((c) => (
              <Link key={c.slug} to={`/collections/${c.slug}`} className="block group cursor-pointer">
                <div className="w-full aspect-square bg-stone overflow-hidden">
                  <img
                    src={c.cover}
                    alt={c.name}
                    title={c.name}
                    className="w-full h-full object-cover object-top group-hover:opacity-90 transition-opacity"
                  />
                </div>
                <div className="mt-4">
                  <h3 className="font-display text-2xl text-black">{c.name}</h3>
                  <p className="mt-[6px] text-sm text-graphite">{c.tagline}</p>
                  <p className="mt-3 text-[12px] font-medium tracking-[0.16em] uppercase text-brass">View Collection</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

export function CollectionDetail() {
  const { slug } = useParams();
  const collection = collections.find((c) => c.slug === slug);

  if (!collection) {
    return (
      <Layout>
        <div className="shell py-32 text-center">
          <p className="eyebrow mb-4">Not Found</p>
          <h1 className="font-display text-4xl text-black">This collection could not be found.</h1>
          <Link to="/collections" className="inline-block mt-8 text-[12px] tracking-[0.2em] uppercase text-brass">
            Back to Collections
          </Link>
        </div>
      </Layout>
    );
  }

  const items = collection.productIds
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean) as typeof products;

  return (
    <Layout>
      <section>
        <div className="shell pt-10">
          <Breadcrumb items={[{ label: "Collections", to: "/collections" }, { label: collection.name }]} />
        </div>
        <div className="w-full aspect-[16/9] bg-stone overflow-hidden mt-8">
          <img
            src={collection.image}
            alt={collection.name}
            title={collection.name}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="shell pt-24 pb-16 text-center">
          <h1 className="font-display text-5xl md:text-6xl text-black">{collection.name}</h1>
          <p className="mt-6 text-graphite max-w-2xl mx-auto leading-relaxed">{collection.intro}</p>
        </div>

        <div className="shell pb-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-16" data-product-shop>
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>
    </Layout>
  );
}